import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, where, orderBy, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Employee } from '../types/employee.ts';
import { TimesheetEntry as TimesheetEntryType } from '../types/timesheet.ts';
import { TimeCalculator, timeUtils } from '../utils/timeCalculations.ts';
import { Save, Upload, Download, Plus, Trash2, Copy } from 'lucide-react';

interface BulkTimesheetRow {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  jobSite: string;
  jobDescription: string;
  startTime: string;
  endTime: string;
  breakTime: number;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  hourlyRate: number;
  totalPay: number;
  notes: string;
  errors: string[];
}

const BulkTimesheetEntry: React.FC = () => {
  const { currentUser } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timesheetRows, setTimesheetRows] = useState<BulkTimesheetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedWeekEnding, setSelectedWeekEnding] = useState('');

  // Common values for quick fill
  const [commonJobSites] = useState([
    'TIAA Frisco Corporate Center FL 1-9',
    'TIAA Frisco Corporate Center FL 10-15',
    'CEC 1275 Mezzanine',
    'Ryan Tax Typical Floor',
    'Carewright',
    'Fortinet',
    'FUB Parkwood',
    'CEC Warehouse Offices',
    'Ryan LLC',
    'Wing Stop',
    'Learfield'
  ]);

  const [commonDescriptions] = useState([
    'Raceways',
    'Wire and Cable',
    'Building Lighting Fixtures',
    'Device Trim Out',
    'Wall Rough',
    'Distribution Equipment',
    'Special Systems',
    'General Conditions'
  ]);

  useEffect(() => {
    fetchEmployees();
    // Set default week ending to next Friday
    const nextFriday = getNextFriday();
    setSelectedWeekEnding(nextFriday.toISOString().split('T')[0]);
  }, []);

  const fetchEmployees = async () => {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        hireDate: doc.data().hireDate?.toDate() || new Date(),
        idExpires: doc.data().idExpires?.toDate(),
        tdlrExpires: doc.data().tdlrExpires?.toDate(),
        scissorLiftTraining: doc.data().scissorLiftTraining?.toDate(),
        firstAidCpr: doc.data().firstAidCpr?.toDate(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Employee[];
      
      // Filter for active employees and sort by name in JavaScript
      const activeEmployees = employeeData
        .filter(emp => emp.status === 'active')
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setEmployees(activeEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
    }
  };

  const getNextFriday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    const nextFriday = new Date(today);
    nextFriday.setDate(today.getDate() + (daysUntilFriday === 0 ? 7 : daysUntilFriday));
    return nextFriday;
  };

  const getWeekDates = (weekEndingDate: string) => {
    const endDate = new Date(weekEndingDate);
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(endDate.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const addEmployeeRows = (employeeIds: string[]) => {
    const weekDates = getWeekDates(selectedWeekEnding);
    const newRows: BulkTimesheetRow[] = [];

    employeeIds.forEach(employeeId => {
      const employee = employees.find(emp => emp.id === employeeId);
      if (!employee) return;

      weekDates.forEach(date => {
        const rowId = `${employeeId}-${date}`;
        if (!timesheetRows.find(row => row.id === rowId)) {
          newRows.push({
            id: rowId,
            employeeId,
            employeeName: employee.name,
            date,
            jobSite: '',
            jobDescription: '',
            startTime: '07:00',
            endTime: '15:30',
            breakTime: 30,
            regularHours: 8,
            overtimeHours: 0,
            totalHours: 8,
            hourlyRate: employee.hourlyWage,
            totalPay: employee.hourlyWage * 8,
            notes: '',
            errors: []
          });
        }
      });
    });

    setTimesheetRows([...timesheetRows, ...newRows]);
  };

  const updateRow = (rowId: string, field: keyof BulkTimesheetRow, value: any) => {
    setTimesheetRows(rows => rows.map(row => {
      if (row.id !== rowId) return row;

      const updatedRow = { ...row, [field]: value };

      // Recalculate hours and pay when time fields change
      if (['startTime', 'endTime', 'breakTime'].includes(field)) {
        try {
          const result = TimeCalculator.calculateComplete(
            updatedRow.startTime,
            updatedRow.endTime,
            updatedRow.breakTime,
            updatedRow.hourlyRate,
            'regular'
          );

          updatedRow.regularHours = result.regularHours;
          updatedRow.overtimeHours = result.overtimeHours;
          updatedRow.totalHours = result.totalHours;
          updatedRow.totalPay = result.totalPay;
          updatedRow.errors = [];
        } catch (error) {
          updatedRow.errors = ['Invalid time calculation'];
        }
      }

      return updatedRow;
    }));
  };

  const removeRow = (rowId: string) => {
    setTimesheetRows(rows => rows.filter(row => row.id !== rowId));
  };

  const duplicateRow = (rowId: string) => {
    const row = timesheetRows.find(r => r.id === rowId);
    if (!row) return;

    const newRow = {
      ...row,
      id: `${row.employeeId}-${Date.now()}`,
      date: timeUtils.getTodayDate()
    };

    setTimesheetRows([...timesheetRows, newRow]);
  };

  const fillColumn = (field: string, value: any) => {
    setTimesheetRows(rows => rows.map(row => {
      const updatedRow = { ...row };
      (updatedRow as any)[field] = value;
      return updatedRow;
    }));
  };

  const submitAllTimesheets = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const batch = writeBatch(db);
      let successCount = 0;
      let errorCount = 0;

      for (const row of timesheetRows) {
        if (row.errors.length > 0 || !row.employeeId || !row.jobSite || !row.jobDescription) {
          errorCount++;
          continue;
        }

        const timesheetEntry: Omit<TimesheetEntryType, 'id'> = {
          employeeId: row.employeeId,
          employeeName: row.employeeName,
          date: new Date(row.date),
          startTime: row.startTime,
          endTime: row.endTime,
          breakTime: row.breakTime,
          jobSite: row.jobSite,
          jobDescription: row.jobDescription,
          workType: 'regular',
          hourlyRate: row.hourlyRate,
          hoursWorked: row.totalHours,
          overtimeHours: row.overtimeHours,
          doubleTimeHours: 0,
          totalPay: row.totalPay,
          status: 'submitted',
          submittedAt: new Date(),
          notes: row.notes || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: currentUser.uid
        };

        const docRef = doc(collection(db, 'timesheets'));
        batch.set(docRef, timesheetEntry);
        successCount++;
      }

      await batch.commit();
      setSuccess(`Successfully submitted ${successCount} timesheets. ${errorCount > 0 ? `${errorCount} entries had errors and were skipped.` : ''}`);
      
      // Clear submitted rows
      setTimesheetRows([]);

    } catch (err) {
      console.error('Error submitting timesheets:', err);
      setError('Failed to submit timesheets');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Employee Name', 'Date', 'Job Site', 'Job Description', 
      'Start Time', 'End Time', 'Break Time', 'Regular Hours', 
      'Overtime Hours', 'Total Hours', 'Hourly Rate', 'Total Pay', 'Notes'
    ];

    const csvContent = [
      headers.join(','),
      ...timesheetRows.map(row => [
        row.employeeName,
        row.date,
        row.jobSite,
        row.jobDescription,
        row.startTime,
        row.endTime,
        row.breakTime,
        row.regularHours,
        row.overtimeHours,
        row.totalHours,
        row.hourlyRate,
        row.totalPay,
        row.notes
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheets-${selectedWeekEnding}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const groupedRows = timesheetRows.reduce((groups, row) => {
    const key = row.employeeName;
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
    return groups;
  }, {} as Record<string, BulkTimesheetRow[]>);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Bulk Timesheet Entry</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Week Ending Date
              </label>
              <input
                type="date"
                value={selectedWeekEnding}
                onChange={(e) => setSelectedWeekEnding(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Add Employees
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addEmployeeRows([e.target.value]);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Employee</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quick Fill Job Site
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    fillColumn('jobSite', e.target.value);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Fill All Job Sites</option>
                {commonJobSites.map(site => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quick Fill Description
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    fillColumn('jobDescription', e.target.value);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Fill All Descriptions</option>
                {commonDescriptions.map(desc => (
                  <option key={desc} value={desc}>{desc}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => addEmployeeRows(employees.map(emp => emp.id))}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add All Employees
            </button>

            <button
              onClick={exportToCSV}
              disabled={timesheetRows.length === 0}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>

            <button
              onClick={submitAllTimesheets}
              disabled={loading || timesheetRows.length === 0}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Submitting...' : 'Submit All'}
            </button>
          </div>
        </div>

        {/* Summary */}
        {timesheetRows.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{timesheetRows.length}</div>
                <div className="text-sm text-gray-600">Total Entries</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {timesheetRows.reduce((sum, row) => sum + row.totalHours, 0).toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Total Hours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {timeUtils.formatCurrency(timesheetRows.reduce((sum, row) => sum + row.totalPay, 0))}
                </div>
                <div className="text-sm text-gray-600">Total Pay</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {timesheetRows.filter(row => row.errors.length > 0).length}
                </div>
                <div className="text-sm text-gray-600">Errors</div>
              </div>
            </div>
          </div>
        )}

        {/* Timesheet Grid */}
        {Object.keys(groupedRows).length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Site</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(groupedRows).map(([employeeName, rows]) => (
                    <React.Fragment key={employeeName}>
                      {rows.map((row, index) => (
                        <tr key={row.id} className={`${row.errors.length > 0 ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index === 0 ? employeeName : ''}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="date"
                              value={row.date}
                              onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="text"
                              value={row.jobSite}
                              onChange={(e) => updateRow(row.id, 'jobSite', e.target.value)}
                              list="jobSites"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Job site"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="text"
                              value={row.jobDescription}
                              onChange={(e) => updateRow(row.id, 'jobDescription', e.target.value)}
                              list="jobDescriptions"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Description"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="time"
                              value={row.startTime}
                              onChange={(e) => updateRow(row.id, 'startTime', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="time"
                              value={row.endTime}
                              onChange={(e) => updateRow(row.id, 'endTime', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input
                              type="number"
                              value={row.breakTime}
                              onChange={(e) => updateRow(row.id, 'breakTime', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              min="0"
                              max="480"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {row.totalHours.toFixed(2)}
                            {row.overtimeHours > 0 && (
                              <div className="text-xs text-orange-600">
                                +{row.overtimeHours.toFixed(2)} OT
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {timeUtils.formatCurrency(row.totalPay)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => duplicateRow(row.id)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Duplicate"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeRow(row.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Data lists for autocomplete */}
        <datalist id="jobSites">
          {commonJobSites.map(site => (
            <option key={site} value={site} />
          ))}
        </datalist>
        <datalist id="jobDescriptions">
          {commonDescriptions.map(desc => (
            <option key={desc} value={desc} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

export default BulkTimesheetEntry;
